import React from 'react'
import { useIntl } from 'react-intl'
import { FormControl, Typography, FormGroup } from '@mui/material'
import { withStyles } from '@mui/styles'
import { FormControlLabel, Checkbox } from '@mui/material'

import { checkListInputStyles } from './check-list-input.styles'

/**
 * @typedef {object} ChecklistOption
 * @property {string | number} key - El identificador único para la opción. La propiedad real se define mediante `cKeyField`.
 * @property {string} value - El texto que se mostrará como etiqueta para el checkbox.
 */

/**
 * @typedef {object} SelectedValue
 * @property {string | number} key - El identificador de la opción seleccionada. La propiedad real se define mediante `keyField`.
 * @property {string} value - El texto de la opción seleccionada. La propiedad real se define mediante `valueField`.
 */

/**
 * @typedef {object} ChangeEvent
 * @property {object} target - Un objeto que simula la estructura de un evento de cambio nativo.
 * @property {string} target.name - El nombre del campo, proveniente de la prop `name`.
 * @property {SelectedValue[]} target.value - El nuevo array de objetos de valor seleccionados.
 */

/**
 * @typedef {object} CheckListInputProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {boolean} [readOnly=false] - Si es `true`, muestra los valores seleccionados como texto simple en lugar de checkboxes interactivos.
 * @property {boolean} [required=false] - Si es `true`, el campo se marca como obligatorio y muestra un error si no se selecciona ninguna opción.
 * @property {SelectedValue[]} [value=[]] - El array de objetos que representan los valores actualmente seleccionados.
 * @property {ChecklistOption[]} [values=[]] - El array de objetos que representan todas las opciones disponibles para seleccionar.
 * @property {string} [cKeyField='key'] - El nombre de la propiedad a usar como clave en los objetos del array `values`.
 * @property {'string' | 'int'} [cKeyType='string'] - El tipo de dato de la clave en el array `values`. Se usa para hacer un cast al manejar el cambio.
 * @property {string} [keyField='key'] - El nombre de la propiedad a usar como clave en los objetos del array `value` (los seleccionados).
 * @property {string} [valueField='value'] - El nombre de la propiedad a usar como valor en los objetos del array `value` (los seleccionados).
 * @property {string} title - El título o etiqueta que se muestra sobre la lista de checkboxes.
 * @property {string} name - El nombre del campo, que se incluirá en el objeto de evento sintético del `onChange`.
 * @property {function(ChangeEvent): void} onChange - La función callback que se ejecuta cuando la selección cambia.
 * @property {'small' | 'normal'} [fontSize] - Controla el tamaño de la fuente del título.
 */

/**
 * Un componente que renderiza una lista de checkboxes para permitir la selección múltiple.
 *
 * Funciona como un componente controlado, gestionando su estado a través de las props `value` y `onChange`.
 * Ofrece un modo de solo lectura (`readOnly`) y validación básica de campo requerido.
 * Es altamente configurable para trabajar con diferentes estructuras de datos de objetos
 * mediante las props `cKeyField`, `keyField`, y `valueField`.
 *
 * @component
 * @example
 * const availableOptions = [
 * { id: 1, label: 'Opción A' },
 * { id: 2, label: 'Opción B' },
 * { id: 3, label: 'Opción C' },
 * ];
 *
 * const MyFormComponent = () => {
 * const [selected, setSelected] = useState([
 * { optionId: 2, optionLabel: 'Opción B' }
 * ]);
 *
 * const handleChange = (e) => {
 * console.log('Campo:', e.target.name, 'Nuevo valor:', e.target.value);
 * setSelected(e.target.value);
 * };
 *
 * // Este componente debe estar envuelto en un <IntlProvider>.
 * return (
 * <CheckListInput
 * title="Seleccione sus preferencias"
 * name="preferences"
 * values={availableOptions}
 * value={selected}
 * onChange={handleChange}
 * cKeyField="id"
 * cKeyType="int"
 * keyField="optionId"
 * valueField="optionLabel"
 * required
 * />
 * );
 * };
 *
 * @param {CheckListInputProps} props - Las props del componente CheckListInput.
 */
export const CheckListInput = withStyles(checkListInputStyles)(({
  classes,
  readOnly = false,
  required = false,
  value = [],
  values = [],
  cKeyField = 'key',
  cKeyType = 'string',
  keyField = 'key',
  valueField = 'value',
  ...props
}) => {
  const { formatMessage } = useIntl()
  const typographyVariant = props.fontSize === 'small' ? '2' : '1'

  const handleChange = (e) => {
    let newValue = [...value]
    if (e.target.checked) {
      newValue.push({
        [keyField]: cKeyType === 'int' ? Number(e.target.name) : e.target.name,
        [valueField]: values.find((v) => v[cKeyField].toString() === e.target.name).value,
      })
    } else if (!e.target.checked) {
      const index = newValue.findIndex((item) => item[keyField].toString() === e.target.name)
      newValue.splice(index, 1)
    }
    props.onChange({ target: { name: props.name, value: newValue } })
  }

  const readOnlyValues = () => {
    let valuesArray = []
    values.forEach((item) => {
      if (value.find((el) => el[keyField].toString() === item[cKeyField].toString()))
        valuesArray.push(item.value)
    })
    return valuesArray
  }

  return (
    <>
      <Typography variant={`subtitle${typographyVariant}`} className={classes.title}>
        {props.title}
      </Typography>
      {readOnly ? (
        readOnlyValues().map((item, i) => (
          <Typography key={i} variant={`body${typographyVariant}`} color="textSecondary">
            {item}
          </Typography>
        ))
      ) : (
        <FormControl required={true} error={required && !value.length}>
          <FormGroup>
            {values.map((item, i) => {
              return (
                <FormControlLabel
                  className={classes.formControlLabel}
                  key={i}
                  control={
                    <Checkbox
                      className={classes.checkbox}
                      color="primary"
                      size="small"
                      checked={
                        value.findIndex(
                          (el) => el[keyField].toString() === item[cKeyField].toString()
                        ) > -1
                      }
                      onChange={handleChange}
                      name={item[cKeyField].toString()}
                    />
                  }
                  label={<Typography variant="body2">{item.value}</Typography>}
                />
              )
            })}
          </FormGroup>
          {required && !value.length ? (
            <Typography variant="caption" color="error">
              {formatMessage({ id: 'checkListInput.required' })}
            </Typography>
          ) : null}
        </FormControl>
      )}
    </>
  )
})
